import "../styles/login.scss";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import actions from "../actions/actions";
import { GuiTextbox } from "../components/global-ui/gui-textbox";
import { useUpdateState } from "../utilities/utilities";
import { Spinner } from "../components/loading-spinner";
import { showToast, ToastContainer } from "../components/toast";
import { GuiButton } from "../components/global-ui/gui-button.jsx";
import PasswordRecoveryHelp from "../components/password-recovery-help";
import { ZendeskSupportUrl } from "../utilities/constants";

export default function VerificationCode() {
  let { loggingIn, resetUsername } = useSelector((store) => store.user);
  let [state, update] = useUpdateState({
    resendStatus: null,
    resending: false,
  });

  const { resendStatus, resending } = state;
  const codeValue = String(state.code || "");
  const isCodeValid = /^\d{6}$/.test(codeValue);

  useEffect(() => {
    if (resetUsername) loadResendStatus();
  }, [resetUsername]);

  async function loadResendStatus() {
    const result =
      await actions.passwordRecovery.getResendStatus(resetUsername);
    if (result?.success) update({ resendStatus: result });
  }

  function updateValue(name, value) {
    if (name === "code") {
      const digitsOnly = String(value || "")
        .replace(/\D/g, "")
        .slice(0, 6);
      update({
        [name]: digitsOnly,
        errors: { ...(state?.errors || {}), [name]: null },
      });
      return;
    }

    update({
      [name]: value,
      errors: { ...(state?.errors || {}), [name]: null },
    });
  }

  function verifyCode() {
    if (loggingIn) return;
    let code = String(state?.code || "")
      .replace(/\D/g, "")
      .slice(0, 6);
    if (!code) {
      update({
        errors: { ...(state?.errors || {}), code: "Code is required" },
      });
      return;
    }

    if (!/^\d{6}$/.test(code)) {
      update({
        errors: {
          ...(state.errors || {}),
          code: "Verification code must be exactly 6 digits",
        },
      });
      return;
    }

    update({ errors: null, code });
    actions.passwordRecovery.verify(code);
  }

  async function resendLink() {
    if (loggingIn || resending) return;
    if (!resetUsername) {
      showToast("error", "Username is required to resend reset link.");
      return;
    }
    update({ resending: true });
    try {
      await actions.passwordRecovery.forgot(resetUsername, {
        showSuccessToast: true,
        successToastMessage: "Reset link sent. Please check your inbox.",
        useGlobalLoading: false,
      });
      await loadResendStatus();
    } finally {
      update({ resending: false });
    }
  }

  const maxResendsPerDay = resendStatus?.maxResendsPerDay ?? 5;
  const resendCount = resendStatus?.resendCount ?? 0;
  const resendExhausted = resendCount >= maxResendsPerDay;

  return (
    <div className="login-shell verification-shell">
      <div className="login-brand verification-brand">
        <img src="/lib/images/Vector.svg" alt="High Ground Solutions" />
        <div className="login-brand-caption">High Ground Solutions</div>
      </div>

      <div className="login-screen sign-in-screen verification-screen">
        <div>
          <span
            className="nav-button"
            onClick={() =>
              !resendExhausted &&
              actions.passwordRecovery.changeScreen("forgotPassword")
            }
            style={
              resendExhausted ? { pointerEvents: "none", opacity: 0.4 } : {}
            }
          >
            <i className="far fa-chevron-left"></i>Back to Forgot Password
          </span>
        </div>

        <h2>Check Your Inbox</h2>
        <p className="verification-lead">
          Enter your verification code below or use the reset link from the
          email we sent.
        </p>

        <div className="verification-code-section">
          <h3>Have a code?</h3>

          <GuiTextbox
            label="Verification Code"
            name="code"
            value={codeValue}
            onChange={updateValue}
            error={state.errors?.code}
            className="code"
            dataQa="verification-code"
            required
          />

          <GuiButton
            onClick={verifyCode}
            disabled={loggingIn || resendExhausted || !isCodeValid}
            dataQa="next"
          >
            {loggingIn ? <Spinner /> : "Next"}
          </GuiButton>

          <div className="verification-footer">
            Did not receive the email? Check your spam filter or{" "}
            <button
              type="button"
              className="verification-link-button"
              onClick={() =>
                actions.passwordRecovery.changeScreen("forgotPassword")
              }
              disabled={resendExhausted}
            >
              try another username
            </button>
          </div>
        </div>

        {resendExhausted ? (
          <div className="verification-inbox verification-resend-section">
            <p className="resend-inline resend-exhausted-msg">
              Maximum resend limit reached. Please contact support for
              assistance.
            </p>
            <a
              className="support-link"
              href={ZendeskSupportUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Contact Support
            </a>
          </div>
        ) : (
          <PasswordRecoveryHelp
            onButtonClick={resendLink}
            resendStatus={resendStatus}
            loading={resending}
            dataQa="resend-link"
          />
        )}

        <ToastContainer />
      </div>
    </div>
  );
}
